// Connection context of SQL editors that are not SqlMonaco.
//
// A plain Monaco SQL editor (job step command, PL/SQL editor / debugger,
// schema- and data-compare script previews, import and job script previews)
// knows which connection and database its SQL belongs to, but Monaco hands a
// DefinitionProvider only the model. Hosts attach their context to their
// editor here; the renderer-wide fallback provider
// (`unownedModelDefinitionProvider.ts`) looks it up by model and resolves
// tables, views, columns and routines against it — through the same
// side-effect-free `jamsql-nav:` location + editor opener path as SqlMonaco,
// so Ctrl/Cmd+hover never navigates there either.
//
// Keyed by EDITOR (with a live `getModel()` and a live context getter) rather
// than by model URI, so a host that swaps models or whose context changes
// (the step's database select) needs no re-registration; the attachment is
// dropped when the editor is disposed.

import type * as monacoNs from 'monaco-editor';

import type { DatabaseEngineId } from '@/core/engines';

export type DefinitionModelContext = {
  readonly connectionId: string;
  /** Database the SQL runs in; `null`/absent = the connection's default. */
  readonly database?: string | null;
  /** Dialect; derived from the connection when absent. */
  readonly engineId?: DatabaseEngineId;
};

/** The two editor members the registry needs (structural, so hosts pass the
 *  `@monaco-editor/react` editor and tests a fake). */
export type DefinitionContextEditor = {
  getModel(): monacoNs.editor.ITextModel | null;
  onDidDispose(listener: () => void): monacoNs.IDisposable;
};

type Attachment = {
  readonly editor: DefinitionContextEditor;
  readonly getContext: () => DefinitionModelContext | null | undefined;
};

const attachments = new Set<Attachment>();

/** Attach a (live) context to an editor. Returns the detach; also detached
 *  automatically when the editor is disposed. */
export function attachDefinitionModelContext(
  editor: DefinitionContextEditor,
  getContext: () => DefinitionModelContext | null | undefined,
): () => void {
  const attachment: Attachment = { editor, getContext };
  attachments.add(attachment);
  const onDispose = editor.onDidDispose(() => {
    attachments.delete(attachment);
  });
  return () => {
    attachments.delete(attachment);
    onDispose.dispose();
  };
}

/** The context of the editor showing `model`, or `null` (no host attached one,
 *  or the host has no connection right now). */
export function lookupDefinitionModelContext(
  model: monacoNs.editor.ITextModel,
): DefinitionModelContext | null {
  for (const attachment of attachments) {
    if (attachment.editor.getModel() !== model) continue;
    const context = attachment.getContext();
    if (context?.connectionId) return context;
  }
  return null;
}

/** Test-only — forget every attachment. */
export function __resetDefinitionModelContextsForTest(): void {
  attachments.clear();
}
